import { useEffect, useState } from "react";
import { jod } from "@astroplanet/shared";
import { money } from "../../lib/format";

const toText = (fils: number | null) => (fils === null ? "" : money(fils));
const parse = (text: string): number | null | undefined => {
  const s = text.trim().replace(/,/g, ".");
  if (s === "") return null;
  if (!/^\d{0,7}(\.\d{0,3})?$/.test(s) || s === ".") return undefined; // undefined = not a valid amount (yet)
  return jod(Number(s));
};

/**
 * An amount in JOD (3 decimals) edited as text and held as integer fils. `null` = empty (only when `allowEmpty`).
 * Typing is never rewritten under the cursor: the text is only reformatted when the value changes from outside.
 */
export function MoneyInput({ id, value, onChange, allowEmpty = false, disabled, placeholder, label }: {
  id?: string; value: number | null; onChange: (fils: number | null) => void; allowEmpty?: boolean; disabled?: boolean; placeholder?: string; label?: string;
}) {
  const [text, setText] = useState(toText(value));
  useEffect(() => {
    const current = parse(text);
    if (current !== value && !(current === undefined && value === null)) setText(toText(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <input
      id={id} className="field num font-mono" inputMode="decimal" value={text} disabled={disabled} placeholder={placeholder} aria-label={label}
      onChange={(e) => {
        setText(e.target.value);
        const v = parse(e.target.value);
        if (v === undefined) return;
        if (v === null && !allowEmpty) return;
        onChange(v);
      }}
      onBlur={() => setText(toText(value))}
    />
  );
}

/** A whole number box that keeps what the user types (empty while editing) and reports only valid integers. */
export function IntInput({ id, value, onChange, min = 0, max = 100000, disabled, label }: { id?: string; value: number; onChange: (n: number) => void; min?: number; max?: number; disabled?: boolean; label?: string }) {
  const [text, setText] = useState(String(value));
  useEffect(() => { if (Number(text) !== value) setText(String(value)); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [value]);
  return (
    <input
      id={id} className="field num font-mono" inputMode="numeric" value={text} disabled={disabled} aria-label={label}
      onChange={(e) => {
        setText(e.target.value);
        const n = Number(e.target.value);
        if (e.target.value.trim() !== "" && Number.isInteger(n) && n >= min && n <= max) onChange(n);
      }}
      onBlur={() => setText(String(value))}
    />
  );
}

export function Switch({ checked, onChange, children, disabled }: { checked: boolean; onChange: (v: boolean) => void; children: React.ReactNode; disabled?: boolean }) {
  return (
    <label className={`flex items-center gap-2 text-xs font-bold ${disabled ? "opacity-60" : ""}`}>
      <input type="checkbox" role="switch" className="rounded text-cosmic-purple focus:ring-0" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      {children}
    </label>
  );
}
